import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { enqueueSnackbar } from 'notistack'
import { useAppSelector } from '../../../app/hooks'
import {
  useCreateFederationConsentUrlMutation,
  useCreateFederationProviderMutation,
  useGetFederationConsentStateQuery,
  useGetFederationProviderTypesQuery,
  useGetVerifiedDomainsQuery,
} from '../../../app/redux-fetch/apiQuery'
import AppStepper from '../../../components/AppStepper/AppStepper'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import { VerifiedDomainStatus } from '../VerifiedDomains/types'
import messages from './messages'
import { CreateFederationProviderRequest, FederationConsentStatus } from './types'

interface FederationProviderWizardProps {
  onDone: () => void
  // E20 F20-7 (WI 794) return leg: reopen against an in-flight consent round trip. initialStep 1 lands on
  // the consent step; the poll's Completed advances to the domains step, a failure renders the retry state.
  initialStep?: number
  consentStateKey?: string
}

// E20 (WI 794): the full-page consent redirect destroys wizard state, and the consent-state poll never
// returns the provider name (which the create still requires) — so stash what the return leg cannot
// re-derive, keyed by the consent-state key, in sessionStorage (same tab + origin across the round trip).
interface ConsentStash {
  name: string
  typeId: number
}

// The create's failure as the Review step shows it. consentInvalid marks the 400 for a consent state that
// cannot back a provider (already bound, e.g. by a create the server compensated) — only a fresh Connect
// Microsoft round trip gets past it.
interface CreateFailure {
  message?: string
  consentInvalid: boolean
}

const consentStashKey = (key: string) => `lsk.federationConsent.${key}`

const readConsentStash = (key: string): ConsentStash | undefined => {
  try {
    const raw = window.sessionStorage.getItem(consentStashKey(key))
    return raw ? (JSON.parse(raw) as ConsentStash) : undefined
  } catch {
    return undefined
  }
}

const writeConsentStash = (key: string, stash: ConsentStash) => {
  try {
    window.sessionStorage.setItem(consentStashKey(key), JSON.stringify(stash))
  } catch {
    // Best-effort: a blocked sessionStorage degrades to re-typing the name on the review step.
  }
}

const clearConsentStash = (key: string) => {
  try {
    window.sessionStorage.removeItem(consentStashKey(key))
  } catch {
    // Best-effort — see writeConsentStash.
  }
}

// How often to re-read the consent state, by what the last read said.
const CONSENT_POLL_ACTIVE_MS = 5000
// A reissued round trip waits on a human in another organisation and can sit unopened for as long as the
// state's TTL, so watch it far more slowly than an attempt the account owner just started.
const CONSENT_POLL_REISSUABLE_MS = 30000

// E20 (story 20.4.3): Declined and Failed no longer end a round trip. The consent-callback error pages
// reissue THIS row - usp_ReissueFederationConsentState mints a fresh State, returns the status to Pending
// and keeps the same ConsentStateKey - so the Microsoft administrator always has a live link to forward.
// The wizard must therefore keep watching those two, or the account owner sees the outcome only after
// reopening the wizard from the key. Completed and Expired are final: the procedure refuses to reissue
// anything else, so stop polling there.
const consentPollForStatus = (status: FederationConsentStatus) => {
  switch (status) {
    case FederationConsentStatus.Completed:
    case FederationConsentStatus.Expired:
      return 0
    case FederationConsentStatus.Declined:
    case FederationConsentStatus.Failed:
      return CONSENT_POLL_REISSUABLE_MS
    default:
      return CONSENT_POLL_ACTIVE_MS
  }
}

// E12 F12-5 (WI 634) + E20 F20-7 (WI 794) — the "add a federation provider" wizard. Four steps: pick the
// provider type + name, Connect Microsoft (the admin-consent round trip, polled by consent-state key and
// resumable after the full-page redirect via the same key), choose which verified domains route to it,
// then review and create. The create binds consentStateKey — the issuer and the Entra tenant come off the
// consent state server-side, never the wire. TenantId + acting persona are set server-side from the
// request context, never sent from here. Plain component state (not Formik) — the flow is multi-step with
// an async round trip mid-way, which a single form model would fight. The E12 BYO branch (Discover + Key
// Vault secret URI) was retired with its provider type (WI 811).
const FederationProviderWizard = ({ onDone, initialStep, consentStateKey }: FederationProviderWizardProps) => {
  const { formatMessage } = useIntl()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const { tenantId, customerId } = loggedInUser

  const { data: types } = useGetFederationProviderTypesQuery()
  const { data: verifiedDomains, refetch: refetchVerifiedDomains } = useGetVerifiedDomainsQuery(
    { tenantId, customerId },
    { skip: !tenantId },
  )
  const [createProvider, { isLoading: isCreating }] = useCreateFederationProviderMutation()
  const [createConsentUrl, { isLoading: isStartingConsent }] = useCreateFederationConsentUrlMutation()

  const resumeStash = useMemo(
    () => (consentStateKey ? readConsentStash(consentStateKey) : undefined),
    [consentStateKey],
  )

  const [activeStep, setActiveStep] = useState(initialStep ?? 0)
  const [typeId, setTypeId] = useState<number | ''>(resumeStash?.typeId ?? '')
  const [name, setName] = useState(resumeStash?.name ?? '')
  const [selectedDomainIds, setSelectedDomainIds] = useState<Set<number>>(new Set())
  // The in-flight consent round trip. consentUrl is absent on the return leg (only the key survives) —
  // by then the URL is spent anyway.
  const [consent, setConsent] = useState<{ consentUrl?: string; consentStateKey: string } | undefined>(
    consentStateKey ? { consentStateKey } : undefined,
  )
  const [consentPollMs, setConsentPollMs] = useState(CONSENT_POLL_ACTIVE_MS)
  const [createFailure, setCreateFailure] = useState<CreateFailure | undefined>()

  const consentKey = consent?.consentStateKey
  // Pending is a 200 body state, so polling never trips the base query's global error toast; the server
  // reports pending-past-expiry as Expired, so no client timer is needed. The cadence follows the last
  // status read (consentPollForStatus) rather than stopping at the first non-Pending one. currentData, not
  // data: data keeps the previous key's result until the new key's first poll lands, so a new round trip
  // would briefly read the old one's Completed and auto-advance before the new consent was approved.
  const { currentData: consentStateData } = useGetFederationConsentStateQuery(consentKey ?? '', {
    skip: !consentKey,
    pollingInterval: consentPollMs,
  })
  // Ignore any cached result once the round trip is abandoned (Try again clears `consent`) — a stale
  // Completed/failed body must not re-drive the step machine or the failure panel.
  const consentState = consent ? consentStateData : undefined

  // One-shot per round trip: activeStep is a dependency and the status stays Completed, so without the
  // ref a Back from the domains step would instantly re-advance and steps 0/1 would be unreachable.
  const autoAdvanced = useRef(false)
  useEffect(() => {
    if (!consentState) return
    setConsentPollMs(consentPollForStatus(consentState.status))
    // The consentUrl we hold carries the State token this outcome just consumed. A reissue mints a new
    // one onto this same row and hands it to the Microsoft administrator on the error page - it never
    // comes back here - so drop ours rather than re-offer a link that can only land on "this link has
    // expired". Returning prev unchanged keeps React from re-rendering when there is nothing to drop.
    if (consentState.status !== FederationConsentStatus.Pending) {
      setConsent((prev) => (prev?.consentUrl ? { consentStateKey: prev.consentStateKey } : prev))
    }
    if (
      consentState.status === FederationConsentStatus.Completed &&
      activeStep === 1 &&
      !autoAdvanced.current
    ) {
      autoAdvanced.current = true
      // The consent leg wrote VerifiedDomain rows after this wizard's initial fetch — refetch so the
      // domains step can list (and pre-check) them (guarded: refetching a skipped query throws).
      if (tenantId) {
        refetchVerifiedDomains()
      }
      setActiveStep(2)
    }
  }, [consentState, activeStep, tenantId, refetchVerifiedDomains])

  // The lookup holds one selectable type ("Microsoft Entra") — preselect it so the first step is just the
  // display name, on the first visit and on a consent return leg whose stash is gone alike.
  useEffect(() => {
    if (typeId !== '' || !types || types.length !== 1) return
    setTypeId(types[0].id)
  }, [types, typeId])

  // Only verified domains can be federated (the server's D50 gate rejects the rest), so the picker offers
  // just those.
  const eligibleDomains = useMemo(
    () => (verifiedDomains || []).filter((d) => d.status === VerifiedDomainStatus.Verified),
    [verifiedDomains],
  )

  // Pre-check the Entra-provenance domains the consent leg auto-verified. Verified rows only — any
  // other status would fail the D50 link gate. Seed once (a deliberate un-check must stick), and
  // only once the refetched list actually contains the new rows.
  const consentDomainsSeeded = useRef(false)
  useEffect(() => {
    if (consentDomainsSeeded.current) return
    if (consentState?.status !== FederationConsentStatus.Completed) return
    const consentVerifiedIds = new Set(
      consentState.verifiedDomains
        .filter((d) => d.status === VerifiedDomainStatus.Verified)
        .map((d) => d.verifiedDomainId),
    )
    if (consentVerifiedIds.size === 0) {
      consentDomainsSeeded.current = true // nothing auto-verified (e.g. all onmicrosoft.com)
      return
    }
    const preChecked = eligibleDomains.filter((d) => consentVerifiedIds.has(d.id)).map((d) => d.id)
    if (preChecked.length === 0) return // the refetch hasn't landed yet
    consentDomainsSeeded.current = true
    setSelectedDomainIds(new Set(preChecked))
  }, [consentState, eligibleDomains])

  const steps = [
    { label: formatMessage(messages.stepType) },
    { label: formatMessage(messages.stepConnect) },
    { label: formatMessage(messages.stepDomains) },
    { label: formatMessage(messages.stepReview) },
  ]

  const toggleDomain = (id: number) => {
    setSelectedDomainIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleConnect = async () => {
    const res = await createConsentUrl({ name: name.trim(), customerId: customerId || undefined })
    // On error the base query already surfaces the toast; stay on the Connect button.
    if (!('data' in res) || !res.data) return
    writeConsentStash(res.data.consentStateKey, { name: name.trim(), typeId: Number(typeId) })
    autoAdvanced.current = false
    setConsentPollMs(CONSENT_POLL_ACTIVE_MS)
    setConsent({ consentUrl: res.data.consentUrl, consentStateKey: res.data.consentStateKey })
  }

  const handleOpenConsentUrl = () => {
    if (consent?.consentUrl) {
      window.location.assign(consent.consentUrl)
    }
  }

  const handleCopyConsentLink = async () => {
    if (!consent?.consentUrl) return
    await navigator.clipboard.writeText(consent.consentUrl)
    enqueueSnackbar(formatMessage(messages.consentLinkCopied), { variant: 'success' })
  }

  // Abandons the round trip and starts a wholly new one. The server can reissue a Declined/Failed state
  // in place, so this is the account owner's choice rather than the only way forward: it drops the key,
  // which means any link already forwarded to the Microsoft administrator stops being watched here.
  const handleConsentRetry = () => {
    if (consentKey) clearConsentStash(consentKey)
    autoAdvanced.current = false
    setConsent(undefined)
    setConsentPollMs(CONSENT_POLL_ACTIVE_MS)
  }

  // The consent this wizard holds can no longer back a provider, so a second create would fail the same
  // way. Start a new round trip from the Connect step instead. The domain selection belonged to the old
  // consent (the new one may connect a different Microsoft organisation), so clear it and let the new
  // Completed consent seed its own.
  const handleReconnect = () => {
    handleConsentRetry()
    setSelectedDomainIds(new Set())
    consentDomainsSeeded.current = false
    setCreateFailure(undefined)
    setActiveStep(1)
  }

  const handleCreate = async () => {
    if (!typeId || !consentKey) return
    const selected = eligibleDomains.filter((d) => selectedDomainIds.has(d.id))
    // The consent state supplies the issuer + Entra tenant server-side; nothing else about the IdP is sent.
    const body: CreateFederationProviderRequest = {
      federationProviderTypeId: typeId,
      name: name.trim(),
      consentStateKey: consentKey,
      customerId: customerId || undefined,
      domainHints: selected.map((d) => ({ domainName: d.domainName, verifiedDomainId: d.id })),
    }
    setCreateFailure(undefined)
    const res = await createProvider(body)
    if ('error' in res && res.error) {
      // Show the failure on the Review step itself: a toast alone is easy to miss, and the wizard would
      // otherwise sit on Review with no sign that the create failed. The body is the ResponseDto envelope.
      const errorBody = (res.error as { data?: any }).data
      const { code, message } = errorBody?.data ?? errorBody ?? {}
      setCreateFailure({
        message: typeof message === 'string' && message.trim() ? message : undefined,
        consentInvalid: code === 'federation_consent_state_invalid',
      })
      return
    }
    clearConsentStash(consentKey)
    enqueueSnackbar(formatMessage(messages.providerCreated), { variant: 'success' })
    onDone()
  }

  const canProceed = (() => {
    switch (activeStep) {
      case 0:
        return Boolean(typeId) && name.trim().length > 0
      case 1:
        return consentState?.status === FederationConsentStatus.Completed
      case 2:
        return selectedDomainIds.size > 0
      default:
        return true
    }
  })()

  const selectedDomains = eligibleDomains.filter((d) => selectedDomainIds.has(d.id))

  const consentFailure = (() => {
    switch (consentState?.status) {
      case FederationConsentStatus.Declined:
        return messages.consentDeclined
      case FederationConsentStatus.Expired:
        return messages.consentExpired
      case FederationConsentStatus.Failed:
        return messages.consentFailed
      default:
        return undefined
    }
  })()

  return (
    <Box sx={{ m: '32px' }}>
      <AppStepper steps={steps} activeStep={activeStep} sx={{ mb: 4 }} />

      {activeStep === 0 && (
        <Stack spacing={3}>
          <TextField
            select
            fullWidth
            label={formatMessage(messages.typeLabel)}
            value={typeId}
            onChange={(e) => setTypeId(Number(e.target.value))}
          >
            {(types || []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label={formatMessage(messages.nameLabel)}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Stack>
      )}

      {activeStep === 1 && (
        <Stack spacing={2}>
          {consentFailure && (
            <>
              <Typography variant='body2' color='error'>
                {formatMessage(consentFailure)}
              </Typography>
              {consentState?.failureReason && (
                <Typography variant='body2' color='text.secondary'>
                  {consentState.failureReason}
                </Typography>
              )}
              {consentState?.correlationId && (
                <Typography variant='caption' color='text.secondary'>
                  {formatMessage(messages.consentReference, {
                    correlationId: consentState.correlationId,
                  })}
                </Typography>
              )}
              <Box>
                <Button variant='outlined' onClick={handleConsentRetry}>
                  {formatMessage(messages.consentRetry)}
                </Button>
              </Box>
            </>
          )}
          {!consentFailure && consent && consentState?.status === FederationConsentStatus.Completed && (
            // Reachable via Back from the domains step (the auto-advance is one-shot) — show the settled
            // state, not a perpetual spinner.
            <Typography variant='body2' color='success.main'>
              {formatMessage(messages.consentCompleted)}
            </Typography>
          )}
          {!consentFailure && consent && consentState?.status !== FederationConsentStatus.Completed && (
            <>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <CircularProgress size={16} />
                <Typography variant='body2' color='text.secondary'>
                  {formatMessage(
                    consent.consentUrl ? messages.consentWaiting : messages.consentWaitingNoLink,
                  )}
                </Typography>
              </Box>
              {consent.consentUrl && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant='contained' onClick={handleOpenConsentUrl}>
                    {formatMessage(messages.openConsentPage)}
                  </Button>
                  <Button variant='outlined' onClick={handleCopyConsentLink}>
                    {formatMessage(messages.copyConsentLink)}
                  </Button>
                </Box>
              )}
            </>
          )}
          {!consentFailure && !consent && (
            <>
              <Typography variant='body2' color='text.secondary'>
                {formatMessage(messages.connectHint)}
              </Typography>
              <Box>
                <Button
                  variant='contained'
                  onClick={handleConnect}
                  disabled={isStartingConsent || name.trim().length === 0}
                >
                  {isStartingConsent
                    ? formatMessage(messages.connectStarting)
                    : formatMessage(messages.connectMicrosoft)}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      )}

      {activeStep === 2 && (
        <Stack spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            {formatMessage(messages.domainsHint)}
          </Typography>
          {eligibleDomains.length === 0 ? (
            <Typography variant='body2' color='warning.main'>
              {formatMessage(messages.noVerifiedDomains)}
            </Typography>
          ) : (
            <FormGroup>
              {eligibleDomains.map((d) => (
                <FormControlLabel
                  key={d.id}
                  control={
                    <Checkbox
                      checked={selectedDomainIds.has(d.id)}
                      onChange={() => toggleDomain(d.id)}
                    />
                  }
                  label={d.domainName}
                />
              ))}
            </FormGroup>
          )}
        </Stack>
      )}

      {activeStep === 3 && (
        <Stack spacing={2}>
          {/* Editable: the name may be missing on the return leg (no stash), and a lost stash must degrade
              to re-typing it, never to a dead 400 (the server requires a name). */}
          <TextField
            fullWidth
            label={formatMessage(messages.nameLabel)}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Box>
            <Typography variant='caption' color='text.secondary'>
              {formatMessage(messages.reviewDomains)}
            </Typography>
            <Typography variant='body2'>
              {selectedDomains.map((d) => d.domainName).join(', ')}
            </Typography>
          </Box>
          {createFailure && (
            <>
              <Typography variant='body2' color='error'>
                {createFailure.message ?? formatMessage(messages.createFailed)}
              </Typography>
              {createFailure.consentInvalid && (
                <Box>
                  <Button variant='outlined' onClick={handleReconnect}>
                    {formatMessage(messages.reconnectMicrosoft)}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>
          {formatMessage(messages.back)}
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button variant='contained' disabled={!canProceed} onClick={() => setActiveStep((s) => s + 1)}>
            {formatMessage(messages.next)}
          </Button>
        ) : (
          <Button
            variant='contained'
            disabled={isCreating || name.trim().length === 0 || createFailure?.consentInvalid}
            onClick={handleCreate}
            startIcon={isCreating ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {isCreating ? formatMessage(messages.creating) : formatMessage(messages.create)}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default FederationProviderWizard
